/* eslint-disable no-useless-escape */
import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import config from "../../../config/Global.json";
import CustomInput from "../../custominput/CustomInput";
import { useNavigate } from "react-router-dom";

const Forgotpassword = () => {
  const [email, setEmail] = useState("");
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [timer, setTimer] = useState(30);
  const [customErr, setCustomErr] = useState(false);
  const [customErrMsg, setCustomErrMsg] = useState("");
  const [errors, setErrors] = useState({});
  const [emailError, setEmailError] = useState("");
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [isUserExist, setIsUserExist] = useState(false); // State for user existence
  const [showTimer, setShowTimer] = useState(false); // State for timer visibility
  const apiBaseUrl = config.api.host;
  const passwordResetEndpoint = config.api.passwordReset;
  const navigate = useNavigate();

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    setCustomErrMsg(""); // Clear message when the email changes
    setIsUserExist(false); // Reset user existence state on email change
    setShowSuccessMessage(false);
  };

  const checkUserExist = async (email) => {
    const emailRegex = /^[\w\.-]+@[a-zA-Z\d\.-]+\.[a-zA-Z]{2,}$/;
    let err = false;
    if (!emailRegex.test(email)) {
      setErrors((prevErrors) => ({ ...prevErrors, email: true }));
      setCustomErr(true);
      setCustomErrMsg("Please enter a valid email.");
      setEmailError("Please enter a valid email.");
      err = true;
    } else {
      setErrors((prevErrors) => ({ ...prevErrors, email: false }));
      setCustomErr(false);
      setCustomErrMsg("");
      setEmailError("");
    }

    if (!err) {
      try {
        const response = await axios.post(
          `${config.api.host}${config.api.userExist}`,
          { email }
        );
        if (response.data.exists) {
          setErrors((prevErrors) => ({ ...prevErrors, email: false }));
          setCustomErr(false);
          setCustomErrMsg(""); // No message for existing user
          setEmailError("");
          setIsUserExist(true); // User exists
        } else {
          // User does not exist
          setErrors((prevErrors) => ({ ...prevErrors, email: true }));
          setCustomErr(true);
          setCustomErrMsg("This user account does not exist."); // Show message if user does not exist
          setEmailError("This user account does not exist.");
          setIsUserExist(false); // User does not exist
        }
      } catch (error) {
        setEmailError("Error checking user existence.");
        toast.error("Error checking user existence.");
      }
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isUserExist && !errors.email && !customErr) {
      // Check if user exists before submitting
      try {
        setIsButtonDisabled(true);
        await axios.post(`${apiBaseUrl}${passwordResetEndpoint}`, { email });
        toast.success("Password reset request sent successfully.");
        setShowSuccessMessage(true);
        setEmail("");
        setTimer(30); // Reset timer to 30 seconds
        setShowTimer(true); // Show the timer after a successful request
      } catch (error) {
        const errorMessage = error.response?.data?.detail || "";
        setCustomErr(true);
        setCustomErrMsg(errorMessage);
        setEmailError(errorMessage);
        setIsButtonDisabled(false);
      }
    }
  };

  useEffect(() => {
    let interval;
    if (showTimer && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (timer === 0) {
      setIsButtonDisabled(false);
      setShowTimer(false); // Hide the timer after it reaches 0
    }
    return () => clearInterval(interval);
  }, [timer, showTimer]);

  return (
    <div className="pv-reset-card">
      <style>{resetRequestStyles}</style>
      <button className="pv-reset-back" type="button" onClick={() => navigate("/login")}>
        <i className="fa-solid fa-arrow-left" />
        Back to login
      </button>

      <div className="pv-reset-icon">
        <i className="fa-solid fa-shield-halved" />
      </div>

      <h2>Reset your password</h2>
      <p className="pv-reset-copy">
        Enter your account email and we will send you a secure link to create a new password.
      </p>

      {showSuccessMessage && (
        <div className="pv-reset-success">
          <i className="fa-solid fa-circle-check" />
          <span>Please check your inbox and spam folder for the reset link.</span>
        </div>
      )}

        <form>
          <div className="pv-reset-field">
            <CustomInput
              type="email"
              id="email"
              placeholder="Email Address"
              value={email}
              onChange={handleEmailChange}
              onBlur={(e) => checkUserExist(e.target.value)}
              custom={customErr}
              msg={customErrMsg} // Display the error message here
              error={!!emailError}
              helpertext={emailError}
            />
          </div>
          <button
            type="button"
            className="pv-reset-primary"
            onFocus={(e) => (e.target.style.boxShadow = "none")}
            onClick={handleSubmit}
            disabled={isButtonDisabled || !isUserExist} // Disable if button is disabled or user does not exist
          >
            Send reset link
          </button>
          <div className="pv-reset-timer">
            {showTimer &&
              timer > 0 && ( // Only show timer when showTimer is true
                <span>
                  Wait for {timer} seconds to request a new one.
                </span>
              )}
          </div>
        </form>
    </div>
  );
};

const resetRequestStyles = `
  .pv-reset-card {
    width: min(460px, 100%);
    margin: 0 auto;
    padding: 8px 2px;
  }

  .pv-reset-back {
    display: inline-flex;
    align-items: center;
    gap: 9px;
    border: 0;
    background: #f4f7f6;
    color: #334155;
    border-radius: 999px;
    padding: 10px 15px;
    font-weight: 800;
    margin-bottom: 28px;
  }

  .pv-reset-icon {
    width: 66px;
    height: 66px;
    border-radius: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #dcfce7, #fef3c7);
    color: #0d532f;
    font-size: 25px;
    margin-bottom: 18px;
  }

  .pv-reset-card h2 {
    color: #0f172a;
    font-size: clamp(30px, 4vw, 42px);
    line-height: 1.05;
    font-weight: 950;
    letter-spacing: -0.03em;
    margin: 0 0 12px;
  }

  .pv-reset-copy {
    color: #64748b;
    font-size: 16px;
    line-height: 1.55;
    margin: 0 0 24px;
    font-weight: 650;
  }

  .pv-reset-success {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    border-radius: 16px;
    background: #ecfdf5;
    color: #047857;
    font-weight: 800;
    margin-bottom: 18px;
  }

  .pv-reset-field {
    margin-bottom: 18px;
  }

  .pv-reset-primary {
    width: 100%;
    min-height: 58px;
    border: 0;
    border-radius: 18px;
    background: #0d532f;
    color: #ffffff;
    font-size: 18px;
    font-weight: 900;
    box-shadow: 0 16px 34px rgba(13, 83, 47, 0.22);
  }

  .pv-reset-primary:disabled {
    cursor: not-allowed;
    background: #94a3b8;
    box-shadow: none;
  }

  .pv-reset-timer {
    min-height: 28px;
    margin-top: 14px;
    color: #64748b;
    text-align: center;
    font-weight: 700;
  }
`;

export default Forgotpassword;
